import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  Switch,
  Radio,
  Row,
  Col,
  Card,
  Button,
  Space,
  Typography,
  Divider,
  Input,
  message,
} from 'antd';
import {
  FileTextOutlined,
  SettingOutlined,
  PrinterOutlined,
  DownloadOutlined,
  UploadOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { Estimate } from '../types';
import { PDFService, PDFLayoutOptions, CompanyInfo } from '../services/pdfService';
import { EmailService, EmailSendRequest } from '../services/emailService';
import { PDFTemplateService } from '../services/pdfTemplateService';
import { PDFSettingsApiService } from '../services/pdfSettingsApi';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface PDFConfigModalProps {
  visible: boolean;
  estimate: Estimate;
  onClose: () => void;
}

interface ExtendedPDFOptions extends PDFLayoutOptions {
  action: 'download' | 'print' | 'email';
  // Email specific options
  emailTo?: string;
  emailCc?: string;
  emailBcc?: string;
  emailSubject?: string;
  emailSenderEmail?: string;
  emailSenderName?: string;
  emailCustomMessage?: string;
  includeCustomMessage?: boolean;
}

interface LogoUploadState {
  file: File | null;
  preview: string | null;
  uploading: boolean;
}

const PDFConfigModal: React.FC<PDFConfigModalProps> = ({
  visible,
  estimate,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [previewLayout, setPreviewLayout] = useState<'professional' | 'compact'>('professional');
  const [logoState, setLogoState] = useState<LogoUploadState>({
    file: null,
    preview: null,
    uploading: false,
  });
  const [emailSending, setEmailSending] = useState(false);
  const [selectedAction, setSelectedAction] = useState<'download' | 'print' | 'email'>('download');

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({
    name: 'CNF Auto LLC',
    address: '123 Main Street',
    city: 'City',
    state: 'State',
    zip: '12345',
    phone: '(555) 123-4567',
    email: 'info@cnfautosuite.com',
  });

  useEffect(() => {
    // Load default settings from backend when modal opens
    if (visible) {
      loadDefaultSettings();
    }
  }, [visible, form]);

  const loadDefaultSettings = async () => {
    try {
      console.log('📥 PDFConfigModal: Loading default PDF settings from backend...');
      const settings = await PDFSettingsApiService.getDefaultSettings();

      console.log('✅ PDFConfigModal: Loaded settings:', settings);
      console.log('   - Color:', settings.color_scheme);
      console.log('   - Logo enabled:', settings.include_company_logo);
      console.log('   - Show internal notes:', settings.show_internal_notes);

      const apiCompanyInfo = PDFSettingsApiService.mapToCompanyInfo(settings);
      const defaultLayoutOptions = PDFSettingsApiService.mapToLayoutOptions(settings);

      // Map API CompanyInfo (optional fields) to pdfService CompanyInfo (required fields)
      const defaultCompanyInfo: CompanyInfo = {
        name: apiCompanyInfo.name,
        address: apiCompanyInfo.address || '123 Main Street',
        city: apiCompanyInfo.city || 'City',
        state: apiCompanyInfo.state || 'State',
        zip: apiCompanyInfo.zip || '12345',
        phone: apiCompanyInfo.phone,
        email: apiCompanyInfo.email,
        website: apiCompanyInfo.website,
        logo: apiCompanyInfo.logo,
      };

      setCompanyInfo(defaultCompanyInfo);
      setPreviewLayout(defaultLayoutOptions.layout);

      // Set logo preview if exists
      if (defaultCompanyInfo.logo) {
        setLogoState({
          file: null,
          preview: defaultCompanyInfo.logo,
          uploading: false,
        });
      } else {
        setLogoState({
          file: null,
          preview: null,
          uploading: false,
        });
      }

      // Update form with default settings
      form.setFieldsValue({
        ...defaultLayoutOptions,
        action: 'download',
        emailSenderEmail: defaultCompanyInfo.email || 'estimates@cnfauto.com',
        emailSenderName: defaultCompanyInfo.name,
        includeCustomMessage: false,
      });

      console.log('✅ PDFConfigModal: Form updated with backend settings');
    } catch (error) {
      console.error('❌ PDFConfigModal: Failed to load settings from backend:', error);
      message.error('Failed to load PDF settings. Using defaults.');

      // Fall back to localStorage if API fails
      const defaultCompanyInfo = PDFTemplateService.getDefaultCompanyInfo();
      const defaultLayoutOptions = PDFTemplateService.getDefaultLayoutOptions();

      setCompanyInfo(defaultCompanyInfo);
      setPreviewLayout(defaultLayoutOptions.layout);

      form.setFieldsValue({
        ...defaultLayoutOptions,
        action: 'download',
        emailSenderEmail: 'estimates@cnfauto.com',
        emailSenderName: defaultCompanyInfo.name,
        includeCustomMessage: false,
      });
    }
  };

  const handleLogoUpload = (file: File) => {
    setLogoState({ ...logoState, uploading: true });

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setLogoState({
        file,
        preview: result,
        uploading: false,
      });

      // Update company info with logo
      setCompanyInfo({
        ...companyInfo,
        logo: result,
      });
    };
    reader.readAsDataURL(file);
    return false; // Prevent upload
  };

  const handleRemoveLogo = () => {
    setLogoState({
      file: null,
      preview: null,
      uploading: false,
    });
    setCompanyInfo({
      ...companyInfo,
      logo: undefined,
    });
  };

  const handleGeneratePDF = async (values: ExtendedPDFOptions) => {
    try {
      setLoading(true);

      const options: PDFLayoutOptions = {
        layout: values.layout,
        includeCompanyLogo: values.includeCompanyLogo,
        showInternalNotes: values.showInternalNotes,
        showPartDetails: values.showPartDetails,
        showDiscountBreakdown: values.showDiscountBreakdown,
        highlightDiscounts: values.highlightDiscounts,
        showOriginalPrices: values.showOriginalPrices,
        colorScheme: values.colorScheme,
        fontFamily: values.fontFamily,
        fontSize: values.fontSize,
        spacing: values.spacing,
        borderStyle: values.borderStyle,
        showPageNumbers: values.showPageNumbers,
      };

      if (values.action === 'email') {
        if (!values.emailTo) {
          message.error('Please enter a recipient email address');
          return;
        }
        setEmailSending(true);

        // Generate PDF buffer first
        const pdfBuffer = await PDFService.generateEstimatePDFBuffer(estimate, options, companyInfo);

        const emailData: EmailSendRequest = {
          estimateId: estimate.id!,
          to: values.emailTo,
          cc: values.emailCc,
          bcc: values.emailBcc,
          subject: values.emailSubject,
          senderEmail: values.emailSenderEmail,
          senderName: values.emailSenderName,
          customMessage: values.emailCustomMessage,
          includeCustomMessage: values.includeCustomMessage || false,
          pdfBuffer: pdfBuffer,
          companyInfo: {
            name: companyInfo.name,
            address: companyInfo.address,
            city: companyInfo.city,
            state: companyInfo.state,
            zip: companyInfo.zip,
            phone: companyInfo.phone,
            email: companyInfo.email,
            website: companyInfo.website,
          },
        };

        await EmailService.sendEstimateEmail(emailData);
        message.success(`Email sent successfully to ${values.emailTo}!`);
      } else {
        await PDFService.generateEstimatePDF(estimate, options, companyInfo);
        message.success('PDF generated successfully!');
      }

      onClose();
    } catch (error) {
      console.error('Error generating PDF:', error);
      if (selectedAction === 'email') {
        message.error('Failed to send email');
      } else {
        message.error('Failed to generate PDF');
      }
    } finally {
      setLoading(false);
      setEmailSending(false);
    }
  };

  const layoutPreviewData = {
    professional: {
      title: 'Professional Layout',
      description: 'Full-featured layout with detailed company information, customer details, and comprehensive service breakdown.',
      features: [
        'Company header with logo',
        'Detailed customer & vehicle info',
        'Complete service breakdown',
        'Parts details (expandable)',
        'Terms & conditions',
        'Professional footer',
      ],
    },
    compact: {
      title: 'Compact Layout',
      description: 'Streamlined layout optimized for quick printing and minimal paper usage.',
      features: [
        'Condensed header',
        'Summary customer info',
        'Simple service list',
        'Essential totals only',
        'Minimal spacing',
        'One-page friendly',
      ],
    },
  };

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          PDF Configuration
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={null}
      centered
      style={{ top: 20 }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleGeneratePDF}
        initialValues={{
          ...PDFTemplateService.getDefaultLayoutOptions(),
          action: 'download',
          emailSenderEmail: 'estimates@cnfauto.com',
          emailSenderName: companyInfo.name,
          includeCustomMessage: false,
        }}
      >
        <Row gutter={[24, 0]}>
          {/* Layout Selection */}
          <Col span={24}>
            <Title level={4}>
              <SettingOutlined style={{ marginRight: 8 }} />
              Layout Options
            </Title>
          </Col>

          <Col span={12}>
            <Form.Item
              label="PDF Layout"
              name="layout"
              tooltip="Choose between professional or compact layout"
            >
              <Radio.Group
                onChange={(e) => setPreviewLayout(e.target.value)}
                buttonStyle="solid"
              >
                <Radio.Button value="professional">Professional</Radio.Button>
                <Radio.Button value="compact">Compact</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Card
              size="small"
              title={layoutPreviewData[previewLayout].title}
              style={{ marginBottom: 16 }}
            >
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {layoutPreviewData[previewLayout].description}
              </Text>
              <div style={{ marginTop: 8 }}>
                {layoutPreviewData[previewLayout].features.map((feature, index) => (
                  <div key={index} style={{ fontSize: '11px', color: '#666' }}>
                    • {feature}
                  </div>
                ))}
              </div>
            </Card>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Color Scheme"
              name="colorScheme"
              tooltip="Choose the primary color for headers and accents"
            >
              <Select>
                <Select.Option value="blue">🔵 Blue Theme</Select.Option>
                <Select.Option value="green">🟢 Green Theme</Select.Option>
                <Select.Option value="gray">⚫ Gray Theme</Select.Option>
                <Select.Option value="red">🔴 Red Theme</Select.Option>
                <Select.Option value="purple">🟣 Purple Theme</Select.Option>
                <Select.Option value="orange">🟠 Orange Theme</Select.Option>
              </Select>
            </Form.Item>

            <Row gutter={[12, 12]}>
              <Col span={24}>
                <Space align="center" style={{ marginBottom: 8 }}>
                  <Form.Item name="includeCompanyLogo" valuePropName="checked" noStyle>
                    <Switch size="small" />
                  </Form.Item>
                  <Text style={{ fontSize: '12px' }}>Include company logo</Text>
                </Space>
              </Col>

              <Col span={24}>
                <Space align="center" style={{ marginBottom: 8 }}>
                  <Form.Item name="showPartDetails" valuePropName="checked" noStyle>
                    <Switch size="small" />
                  </Form.Item>
                  <Text style={{ fontSize: '12px' }}>Show detailed parts breakdown</Text>
                </Space>
              </Col>

              <Col span={24}>
                <Space align="center" style={{ marginBottom: 8 }}>
                  <Form.Item name="showInternalNotes" valuePropName="checked" noStyle>
                    <Switch size="small" />
                  </Form.Item>
                  <Text style={{ fontSize: '12px' }}>Include internal notes</Text>
                </Space>
              </Col>

              <Col span={24}>
                <Space align="center" style={{ marginBottom: 8 }}>
                  <Form.Item name="showPageNumbers" valuePropName="checked" noStyle>
                    <Switch size="small" />
                  </Form.Item>
                  <Text style={{ fontSize: '12px' }}>Show page numbers</Text>
                </Space>
              </Col>
            </Row>

            {/* Discount Display Options */}
            <Divider style={{ margin: '16px 0' }} />
            <Title level={5} style={{ marginBottom: '12px' }}>
              Discount Display Options
            </Title>
            <Row gutter={[8, 8]}>
              <Col span={24}>
                <Space align="center" style={{ marginBottom: 8 }}>
                  <Form.Item name="showDiscountBreakdown" valuePropName="checked" noStyle>
                    <Switch size="small" />
                  </Form.Item>
                  <Text style={{ fontSize: '12px' }}>Show detailed discount breakdown</Text>
                </Space>
              </Col>

              <Col span={24}>
                <Space align="center" style={{ marginBottom: 8 }}>
                  <Form.Item name="highlightDiscounts" valuePropName="checked" noStyle>
                    <Switch size="small" />
                  </Form.Item>
                  <Text style={{ fontSize: '12px' }}>Highlight discounted items</Text>
                </Space>
              </Col>

              <Col span={24}>
                <Space align="center" style={{ marginBottom: 8 }}>
                  <Form.Item name="showOriginalPrices" valuePropName="checked" noStyle>
                    <Switch size="small" />
                  </Form.Item>
                  <Text style={{ fontSize: '12px' }}>Show original prices with strikethrough</Text>
                </Space>
              </Col>
            </Row>
          </Col>

          {/* Typography & Styling Options */}
          <Col span={24}>
            <Divider />
            <Title level={4}>Typography & Styling</Title>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Customize fonts, sizes, and spacing for your PDF
            </Text>
          </Col>

          <Col span={8}>
            <Form.Item
              label="Font Family"
              name="fontFamily"
              tooltip="Choose the font style for your PDF"
            >
              <Select size="small">
                <Select.Option value="helvetica">Helvetica (Modern)</Select.Option>
                <Select.Option value="times">Times (Traditional)</Select.Option>
                <Select.Option value="courier">Courier (Monospace)</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label="Font Size"
              name="fontSize"
              tooltip="Adjust the overall size of text in the PDF"
            >
              <Select size="small">
                <Select.Option value="small">Small</Select.Option>
                <Select.Option value="medium">Medium</Select.Option>
                <Select.Option value="large">Large</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label="Spacing"
              name="spacing"
              tooltip="Control line spacing and section gaps"
            >
              <Select size="small">
                <Select.Option value="tight">Tight</Select.Option>
                <Select.Option value="normal">Normal</Select.Option>
                <Select.Option value="loose">Loose</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Border Style"
              name="borderStyle"
              tooltip="Choose the border style for tables and sections"
            >
              <Select size="small">
                <Select.Option value="none">No Borders</Select.Option>
                <Select.Option value="simple">Simple Lines</Select.Option>
                <Select.Option value="rounded">Rounded Corners</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          {/* Company Information */}
          <Col span={24}>
            <Divider />
            <Title level={4}>Company Information</Title>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Customize company details that will appear on the PDF
            </Text>
          </Col>

          <Col span={12}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Input
                placeholder="Company Name"
                value={companyInfo.name}
                onChange={(e) => setCompanyInfo({ ...companyInfo, name: e.target.value })}
                size="small"
              />
              <Input
                placeholder="Address"
                value={companyInfo.address}
                onChange={(e) => setCompanyInfo({ ...companyInfo, address: e.target.value })}
                size="small"
              />
              <Row gutter={8}>
                <Col span={8}>
                  <Input
                    placeholder="City"
                    value={companyInfo.city}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, city: e.target.value })}
                    size="small"
                  />
                </Col>
                <Col span={8}>
                  <Input
                    placeholder="State"
                    value={companyInfo.state}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, state: e.target.value })}
                    size="small"
                  />
                </Col>
                <Col span={8}>
                  <Input
                    placeholder="ZIP"
                    value={companyInfo.zip}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, zip: e.target.value })}
                    size="small"
                  />
                </Col>
              </Row>
            </Space>
          </Col>

          <Col span={12}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Input
                placeholder="Phone Number"
                value={companyInfo.phone}
                onChange={(e) => setCompanyInfo({ ...companyInfo, phone: e.target.value })}
                size="small"
              />
              <Input
                placeholder="Email Address"
                value={companyInfo.email}
                onChange={(e) => setCompanyInfo({ ...companyInfo, email: e.target.value })}
                size="small"
              />
              <Input
                placeholder="Website (optional)"
                value={companyInfo.website || ''}
                onChange={(e) => setCompanyInfo({ ...companyInfo, website: e.target.value })}
                size="small"
              />

              {/* Logo Upload Section */}
              <div style={{ marginTop: 8 }}>
                <Text style={{ fontSize: '12px', marginBottom: 4, display: 'block' }}>Company Logo</Text>
                {logoState.preview ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img
                      src={logoState.preview}
                      alt="Logo preview"
                      style={{ width: 40, height: 40, objectFit: 'contain', border: '1px solid #d9d9d9', borderRadius: 4 }}
                    />
                    <Button size="small" onClick={handleRemoveLogo}>
                      Remove
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="small"
                    icon={<UploadOutlined />}
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) handleLogoUpload(file);
                      };
                      input.click();
                    }}
                    loading={logoState.uploading}
                  >
                    Upload Logo
                  </Button>
                )}
              </div>
            </Space>
          </Col>

          {/* Action Selection */}
          <Col span={24}>
            <Divider />
            <Title level={4}>
              <PrinterOutlined style={{ marginRight: 8 }} />
              Action
            </Title>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Choose what to do with the generated PDF
            </Text>
          </Col>

          <Col span={24}>
            <Form.Item
              label="Action"
              name="action"
              tooltip="Choose whether to download or email the PDF"
            >
              <Radio.Group
                onChange={(e) => setSelectedAction(e.target.value)}
                buttonStyle="solid"
              >
                <Radio.Button value="download">
                  <DownloadOutlined style={{ marginRight: 4 }} />
                  Download
                </Radio.Button>
                <Radio.Button value="email">
                  <PictureOutlined style={{ marginRight: 4 }} />
                  Email
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>

          {/* Email Configuration */}
          {selectedAction === 'email' && (
            <>
              <Col span={24}>
                <Card
                  size="small"
                  title="Email Configuration"
                  style={{ backgroundColor: '#f8f9fa' }}
                >
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <Form.Item
                        label="To (Required)"
                        name="emailTo"
                        rules={[{ required: true, type: 'email', message: 'Please enter a valid email address' }]}
                      >
                        <Input placeholder="customer@example.com" size="small" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="CC (Optional)"
                        name="emailCc"
                        rules={[{ type: 'email', message: 'Please enter a valid email address' }]}
                      >
                        <Input placeholder="manager@example.com" size="small" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="BCC (Optional)"
                        name="emailBcc"
                        rules={[{ type: 'email', message: 'Please enter a valid email address' }]}
                      >
                        <Input placeholder="admin@example.com" size="small" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Subject"
                        name="emailSubject"
                      >
                        <Input placeholder={`Service Estimate #${estimate.estimate_number}`} size="small" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Sender Email"
                        name="emailSenderEmail"
                        rules={[{ type: 'email', message: 'Please enter a valid email address' }]}
                      >
                        <Input placeholder="estimates@cnfauto.com" size="small" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Sender Name"
                        name="emailSenderName"
                      >
                        <Input placeholder={companyInfo.name} size="small" />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name="includeCustomMessage"
                        valuePropName="checked"
                        style={{ marginBottom: 8 }}
                      >
                        <Switch size="small" />
                        <Text style={{ marginLeft: 8, fontSize: '12px' }}>Include custom message</Text>
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label="Custom Message"
                        name="emailCustomMessage"
                        tooltip="This message will be included in the email body"
                      >
                        <TextArea
                          rows={3}
                          placeholder="Add a personal message for the customer..."
                          size="small"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </>
          )}

          {/* Actions */}
          <Col span={24}>
            <Divider />
            <Row gutter={12} justify="end">
              <Col>
                <Button onClick={onClose}>
                  Cancel
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading || emailSending}
                  icon={selectedAction === 'email' ? <PictureOutlined /> : <DownloadOutlined />}
                >
                  {selectedAction === 'email' ?
                    (emailSending ? 'Sending Email...' : 'Send Email') :
                    'Generate PDF'
                  }
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default PDFConfigModal;